"use client";

import { useMemo } from "react";

import { FadeInIntersection } from "@/common/components";
import { Button } from "@/common/components-2/button";
import { MigrationLink } from "@/common/components-2/migration-link";
import { Section } from "@/common/components-2/section";
import { SectionHeading } from "@/common/components-2/section-heading";
import {
  ContentComponent,
  DescriptionComponent,
  HeadingComponent,
  ImageComponent,
  TitleComponent,
  WrapperComponent,
} from "@/modules/homepage/components-2/how-it-works-section";
import { useTranslations } from "@/services/i18n";

export function TypeSection() {
  const t = useTranslations("pages.consultations.type");
  const items = useMemo(
    () => [
      {
        title: {
          text: t("items.1.title"),
          icon: "ph:phone-call",
        },
        heading: t.raw("items.1.heading") as string,
        description: t.raw("items.1.description") as string,
        image: {
          url: "https://assets-cdn.upacjenta.pl/8092e587-6fae-4d7b-b524-17f44eb0e9b8.png",
          alt: "",
          width: 0,
          height: 0,
        },
        button: { label: t("items.1.button"), url: "https://upacjenta.pl/konsultacje-online/wynikow" },
        price: t.rich("items.1.price"),
      },
      {
        title: {
          text: t("items.2.title"),
          icon: "ph:envelope-open",
        },
        heading: t.raw("items.2.heading") as string,
        description: t.raw("items.2.description") as string,
        image: {
          url: "https://assets-cdn.upacjenta.pl/4a86e501-ad0b-4078-8a5e-32a6f3604095.png",
          alt: "",
          width: 0,
          height: 0,
        },
        button: { label: t("items.2.button"), url: "https://buy.stripe.com/dR63dQc3ecmm5Gw8wx" },
        price: t.rich("items.2.price"),
      },
    ],
    [t],
  );

  return (
    <FadeInIntersection rootMargin="0px 0px -300px 0px" delay={100} asChild animation="fadeUp">
      <Section classes={{ section: "py-10 md:py-14 lg:py-16" }}>
        <SectionHeading heading={t.rich("heading")} description={t("description")} />
        <div>
          {items.map(({ title, heading, description, image, button, price }, index) => (
            <WrapperComponent
              className="my-2 lg:py-20 lg:last-of-type:pb-0 lg:first-of-type:py-12 xl:py-24"
              key={title.text}
            >
              <ContentComponent index={index + 1} className="mb-10 lg:mb-0">
                <TitleComponent title={title.text} icon={title.icon} className="justify-center lg:justify-start" />
                <HeadingComponent heading={heading} className="text-4xl lg:text-5xl" />
                <DescriptionComponent description={description} className="mt-6 lg:text-left" />
                <div className="mt-14 flex flex-col gap-4 md:mt-12 md:flex-row">
                  <Button
                    variant="primary"
                    className={`w-full max-md:order-2 md:w-max ${index % 2 === 0 ? "order-1" : "order-2"}`}
                    size="lg"
                    asChild
                  >
                    <MigrationLink href={button.url}>{button.label}</MigrationLink>
                  </Button>
                  <div
                    className={`flex h-12 w-max items-center justify-center gap-2 rounded-full bg-secondary px-8 max-md:order-1 max-md:self-end [&>span]:font-semibold [&>span]:text-2xl ${index % 2 === 0 ? "order-2" : "order-1"} [&>span]:text-nowrap`}
                  >
                    {price}
                  </div>
                </div>
              </ContentComponent>
              <ImageComponent index={index + 1} image={image} />
            </WrapperComponent>
          ))}
        </div>
      </Section>
    </FadeInIntersection>
  );
}
